import { VersionFetcherConfig } from "../types/update.types";

/**
 * Custom error class for version mismatch scenarios.
 * Thrown when the fetched version differs from the current version.
 */
export class VersionMismatchError extends Error {
  constructor(
    message: string,
    public readonly newVersion: string
  ) {
    super(message);
    this.name = "VersionMismatchError";
  }
}

/**
 * Service for fetching and validating application versions.
 * Supports abort functionality for cancelling in-progress version checks.
 */
export class VersionFetcher {
  private config: VersionFetcherConfig;
  private abortController: AbortController | null = null;

  /**
   * Creates a new VersionFetcher instance.
   * @param config Configuration object containing version file URL and current version
   */
  constructor(config: VersionFetcherConfig) {
    this.config = config;
  }

  /**
   * Checks if the current version matches the version from the remote source.
   * @throws {VersionMismatchError} When versions don't match
   * @throws {Error} When version fetch fails
   */
  public async checkVersion(): Promise<void> {
    try {
      const version = await this.fetchVersion();
      this.setCurrentVersionWhenNotSet(version);
      this.validateVersion(version);
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Aborts any ongoing version check operation.
   */
  public abort(): void {
    this.abortController?.abort();
  }

  private setCurrentVersionWhenNotSet(version: string): void {
    if (this.config.currentVersion) {
      return;
    }

    this.setCurrentVersion(version);
  }

  public setCurrentVersion(version: string): void {
    this.config.currentVersion = version;
  }

  /**
   * Fetches the version string from the configured URL.
   * @returns Promise resolving to the version string
   * @throws {Error} When fetch fails or returns non-OK status
   */
  private async fetchVersion(): Promise<string> {
    this.abortController = new AbortController();

    const response = await fetch(this.config.versionFileUrl, {
      signal: this.abortController.signal,
    });

    if (!response || !response.ok) {
      throw new Error(`Failed to fetch version: ${response?.statusText}`);
    }

    return response.text();
  }

  /**
   * Validates the fetched version against the current version.
   * @param newVersion The version string fetched from remote source
   * @throws {VersionMismatchError} When versions don't match
   */
  private validateVersion(newVersion: string): void {
    if (
      this.config.currentVersion &&
      newVersion !== this.config.currentVersion
    ) {
      throw new VersionMismatchError("Version mismatch detected", newVersion);
    }
  }
}
